import AsyncStorage from "@react-native-async-storage/async-storage";
import { Fragment, useEffect, useState } from "react";
import {
    ActivityIndicator,
    Modal,
    Pressable,
    ScrollView,
    StyleSheet,
    Text,
    TouchableOpacity,
    View,
} from "react-native";
import { AppBar } from "../components";
import { CheckCircleIcon, CheckCircleOutlineIcon, CloseIcon, AccessibilityIcon, GradingIcon, ExtensionIcon, ArrowRightIcon } from "../assets/icons";
import { useNavigation } from "@react-navigation/native";
import { buildStudentStatistic } from "../Utils/characterStatistic";
import { getQuizIndicatorStatistics } from "../services/survey";
import i18n from "../translation/index";

const dummyTeacherEntries = [
    { id: 1, nama: "Budi Suharjo, S.Pd", nilai: "SERING MUNCUL", tanggal: "14 Mar 2026, Pukul 12:24" },
    { id: 2, nama: "Sri Yuniasih, S.Pd", nilai: "KONSISTEN MUNCUL", tanggal: "20 Mar 2026, Pukul 16:20" },
    { id: 3, nama: "Muhammad Hisyam, S.Pd", nilai: "SERING MUNCUL", tanggal: "21 Mar 2026, Pukul 07:00" },
];

const withTeacherHistoryPreview = (character) => {
    if (character.pengisianGuru?.length) return character;
    const indicators = (character.indikator || []).filter((indicator) => indicator.isChecked);
    return {
        ...character,
        isTeacherHistoryDummy: indicators.length > 0,
        pengisianGuru: indicators.length > 0
            ? dummyTeacherEntries.map((teacher, index) => ({
                ...teacher,
                indikator: indicators[index % indicators.length].text,
            }))
            : [],
    };
};

const SurveyKarakterDetail = ({ route }) => {
    const { params } = route;
    const studentId = params?.studentId;
    const navigation = useNavigation();
    const [loading, setLoading] = useState(!params?.studentStatistic);
    const [loadError, setLoadError] = useState(false);
    const [modalKarakter, setModalKarakter] = useState(null);

    const [student, setStudent] = useState(() => params?.studentStatistic || {
        id: studentId,
        ...buildStudentStatistic(null, params?.studentName || "", params?.additionalContentId),
    });

    useEffect(() => {
        let active = true;
        const controller = new AbortController();
        setLoadError(false);
        if (params?.studentStatistic) {
            setStudent(params.studentStatistic);
            setLoading(false);
            return undefined;
        }
        setStudent({ id: studentId, ...buildStudentStatistic(null, params?.studentName || "", params?.additionalContentId) });
        if (!params?.additionalContentId) {
            setLoadError(true);
            setLoading(false);
            return undefined;
        }
        setLoading(true);
        getQuizIndicatorStatistics(params.additionalContentId, controller.signal)
            .then(({ data }) => {
                if (active) setStudent({
                    id: studentId,
                    ...buildStudentStatistic(data, params?.studentName || "", params.additionalContentId),
                });
            })
            .catch(() => {
                if (active) setLoadError(true);
            })
            .finally(() => {
                if (active) setLoading(false);
            });
        return () => {
            active = false;
            controller.abort();
        };
    }, [params?.additionalContentId, params?.studentName, params?.studentStatistic, studentId]);

    useEffect(() => {
        async function getLang() {
            return await AsyncStorage.getItem("lang");
        }
        getLang().then((res) => {
            if (!res) {
                AsyncStorage.setItem("lang", "id");
                i18n.changeLanguage("id");
                return;
            }
            AsyncStorage.setItem("lang", res);
            i18n.changeLanguage(res);
        });
    }, []);

    return (
        <Fragment>
            <AppBar title={"Detail Capaian Siswa"} isBack={true} />
            {loading ? (
                <View style={styles.loading}>
                    <ActivityIndicator color="#6724E2" />
                </View>
            ) : (
                <ScrollView style={{ flex: 1, backgroundColor: "#F5F5F5" }} contentContainerStyle={{ padding: 16, gap: 12 }}>
                    {loadError && <Text>Data capaian siswa gagal dimuat. Silakan coba kembali.</Text>}
                    <View style={styles.card}>
                        <View style={styles.rowCenter}>
                            <CheckCircleIcon fill={student.isAnswered ? "#4EB826" : "#C2C2C2"} width={32} height={32} />
                            <Text style={[styles.studentName, student.isAnswered && styles.studentNameActive]}>
                                {student.name}
                            </Text>
                        </View>

                        <View style={[styles.rowCenter, { marginTop: 12, gap: 0 }]}>
                            <View style={styles.summaryBox}>
                                <View style={styles.summaryBadge}>
                                    <Text style={styles.summaryBadgeTx}>Pencapaian Karakter</Text>
                                </View>
                                <View style={[styles.rowCenter, { marginTop: 12, gap: 10 }]}>
                                    <AccessibilityIcon width={36} height={36} />
                                    <View>
                                        <Text style={styles.summaryCount}>{student.karakterTerisi} dari {student.totalKarakter}</Text>
                                        <Text style={styles.summaryLabel}>Sudah Terisi</Text>
                                    </View>
                                </View>
                                <Text style={styles.summaryDesc}>
                                    {student.totalKarakter - student.karakterTerisi === 0
                                        ? "Semua karakter sudah terisi"
                                        : `Tersisa ${student.totalKarakter - student.karakterTerisi} karakter belum diisi`}
                                </Text>
                            </View>

                            <View style={styles.dividerV} />

                            <View style={styles.summaryBox}>
                                <View style={styles.summaryBadge}>
                                    <Text style={styles.summaryBadgeTx}>Pencapaian Indikator</Text>
                                </View>
                                <View style={[styles.rowCenter, { marginTop: 12, gap: 10 }]}>
                                    <GradingIcon width={36} height={36} />
                                    <View>
                                        <Text style={styles.summaryCount}>{student.indikatorTerisi} dari {student.totalIndikator}</Text>
                                        <Text style={styles.summaryLabel}>Sudah Terisi</Text>
                                    </View>
                                </View>
                                <Text style={styles.summaryDesc}>
                                    {student.totalIndikator - student.indikatorTerisi === 0
                                        ? "Semua indikator sudah terisi"
                                        : `Tersisa ${student.totalIndikator - student.indikatorTerisi} karakter belum diisi`}
                                </Text>
                            </View>
                        </View>
                    </View>

                    {student.karakter.map((kar) => (
                        <View key={kar.id} style={styles.card}>
                            <Text style={styles.karHeader}>
                                <Text style={styles.karHeaderLabel}>Karakter {kar.id}</Text>
                                <Text style={styles.karHeaderDot}> • </Text>
                                <Text style={styles.karHeaderName}>{kar.nama}</Text>
                            </Text>

                            <View style={[styles.rowCenter, { gap: 8, marginTop: 8, backgroundColor: "#F3F3F3", borderRadius: 8, paddingHorizontal: 12, paddingVertical: 8, alignSelf: "flex-start" }]}>
                                <ExtensionIcon width={18} height={18} />
                                <Text style={styles.indikatorCount}>
                                    {kar.indikatorTerisi} dari {kar.totalIndikator} indikator terisi
                                </Text>
                            </View>

                            <View style={styles.dividerH} />

                            <Text style={styles.indikatorSectionLabel}>Indikator</Text>

                            {kar.indikator.map((ind) => (
                                <View key={ind.id} style={[styles.rowTop, { gap: 10, marginTop: 8 }]}>
                                    <CheckCircleOutlineIcon
                                        fill={ind.isChecked ? "#4EB826" : "#C2C2C2"}
                                        width={20}
                                        height={20}
                                        style={{ flexShrink: 0, marginTop: 1 }}
                                    />
                                    <Text style={[styles.indikatorTx, ind.isChecked && styles.indikatorTxChecked]}>
                                        {ind.text}
                                    </Text>
                                </View>
                            ))}

                            <TouchableOpacity
                                style={[styles.rowCenter, { gap: 8, marginTop: 14 }]}
                                onPress={() => setModalKarakter(withTeacherHistoryPreview(kar))}
                            >
                                <ArrowRightIcon width={18} height={18} fill="#6724E2" />
                                <Text style={styles.lihatGuru}>Lihat Hasil Pengisian Guru</Text>
                            </TouchableOpacity>
                        </View>
                    ))}
                </ScrollView>
            )}

            <Modal
                visible={!!modalKarakter}
                transparent
                animationType="fade"
                onRequestClose={() => setModalKarakter(null)}
            >
                <Pressable style={styles.modalOverlay} onPress={() => setModalKarakter(null)}>
                    <Pressable style={styles.modalPopup} onPress={(e) => e.stopPropagation()}>
                        {/* Modal header */}
                        <View style={styles.modalHeader}>
                            <Text style={styles.modalTitle}>Karakter {modalKarakter?.nama}</Text>
                            <TouchableOpacity onPress={() => setModalKarakter(null)}>
                                <CloseIcon width={20} height={20} fill="#323232" />
                            </TouchableOpacity>
                        </View>

                        <View style={styles.modalDivider} />

                        <ScrollView style={{ maxHeight: 380 }} contentContainerStyle={{ gap: 16 }} showsVerticalScrollIndicator={false}>
                            {modalKarakter?.pengisianGuru?.length === 0 && (
                                <Text style={{ fontFamily: "Archivo-Regular", fontSize: 13, color: "#999", textAlign: "center", paddingVertical: 20 }}>
                                    Belum ada pengisian dari guru
                                </Text>
                            )}
                            {modalKarakter?.pengisianGuru?.map((guru, idx) => {
                                const isLast = idx === (modalKarakter.pengisianGuru.length - 1);
                                return (
                                    <View key={guru.id} style={[styles.guruItem, isLast && { borderBottomWidth: 0, paddingBottom: 0 }]}>
                                        <Text style={styles.guruName}>{guru.nama}</Text>
                                        <Text style={styles.guruIndikator}>
                                            Indikator: {guru.indikator}
                                        </Text>
                                        <Text style={styles.guruNilai}>Nilai: {guru.nilai}</Text>
                                        <Text style={styles.guruTanggal}>Tanggal input: {guru.tanggal}</Text>
                                    </View>
                                );
                            })}
                        </ScrollView>
                    </Pressable>
                </Pressable>
            </Modal>
        </Fragment>
    );
};

const styles = StyleSheet.create({
    loading: {
        flex: 1,
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "#FFF",
    },
    card: {
        backgroundColor: "#FFF",
        borderRadius: 12,
        padding: 16,
        borderWidth: 1,
        borderColor: "#EAEAEA",
    },
    rowCenter: {
        flexDirection: "row",
        alignItems: "center",
        gap: 10,
    },
    rowTop: {
        flexDirection: "row",
        alignItems: "flex-start",
    },
    checkWrap: {
        width: 32,
        height: 32,
        borderRadius: 16,
        backgroundColor: "#EAEAEA",
        alignItems: "center",
        justifyContent: "center",
    },
    checkWrapActive: {
        backgroundColor: "#4EB826",
    },
    studentName: {
        fontFamily: "Archivo-SemiBold",
        fontSize: 14,
        color: "#323232",
    },
    studentNameActive: {
        color: "#4EB826",
    },
    summaryBox: {
        flex: 1,
        paddingHorizontal: 6,
    },
    summaryBadge: {
        alignSelf: "flex-start",
        backgroundColor: "#EEEAFF",
        paddingHorizontal: 10,
        paddingVertical: 5,
        borderRadius: 6,
    },
    summaryBadgeTx: {
        fontFamily: "Archivo-SemiBold",
        fontSize: 11,
        color: "#6724E2",
    },
    summaryCount: {
        fontFamily: "Archivo-Bold",
        fontSize: 16,
        color: "#323232",
    },
    summaryLabel: {
        fontFamily: "Archivo-Regular",
        fontSize: 11,
        color: "#323232",
    },
    summaryDesc: {
        fontFamily: "Archivo-Regular",
        fontSize: 11,
        color: "#999",
        marginTop: 8,
    },
    dividerV: {
        width: 1,
        alignSelf: "stretch",
        backgroundColor: "#EAEAEA",
        marginHorizontal: 4,
    },
    dividerH: {
        height: 1,
        backgroundColor: "#EAEAEA",
        marginVertical: 12,
    },
    karHeader: {
        fontSize: 13,
    },
    karHeaderLabel: {
        fontFamily: "Archivo-SemiBold",
        color: "#6724E2",
    },
    karHeaderDot: {
        fontFamily: "Archivo-Regular",
        color: "#6724E2",
    },
    karHeaderName: {
        fontFamily: "Archivo-SemiBold",
        color: "#6724E2",
    },
    indikatorCount: {
        fontFamily: "Archivo-Regular",
        fontSize: 12,
        color: "#555",
    },
    indikatorSectionLabel: {
        fontFamily: "Archivo-SemiBold",
        fontSize: 12,
        color: "#323232",
        marginBottom: 4,
    },
    indikatorTx: {
        flex: 1,
        fontFamily: "Archivo-Regular",
        fontSize: 12,
        color: "#C2C2C2",
        lineHeight: 18,
    },
    indikatorTxChecked: {
        color: "#323232",
    },
    lihatGuru: {
        fontFamily: "Archivo-SemiBold",
        fontSize: 12,
        color: "#6724E2",
    },
    modalOverlay: {
        flex: 1,
        backgroundColor: "rgba(0,0,0,0.45)",
        justifyContent: "center",
        alignItems: "center",
        paddingHorizontal: 24,
    },
    modalPopup: {
        backgroundColor: "#FFF",
        borderRadius: 16,
        padding: 20,
        width: "100%",
        maxWidth: 420,
        shadowColor: "#000",
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.15,
        shadowRadius: 12,
        elevation: 8,
    },
    modalDivider: {
        height: 1,
        backgroundColor: "#EAEAEA",
        marginBottom: 16,
    },
    modalHeader: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        marginBottom: 16,
    },
    modalTitle: {
        fontFamily: "Archivo-Bold",
        fontSize: 15,
        color: "#323232",
    },
    guruItem: {
        paddingBottom: 16,
        borderBottomWidth: 1,
        borderBottomColor: "#EAEAEA",
        gap: 4,
    },
    guruName: {
        fontFamily: "Archivo-Bold",
        fontSize: 13,
        color: "#323232",
    },
    guruIndikator: {
        fontFamily: "Archivo-Regular",
        fontSize: 12,
        color: "#555",
        lineHeight: 17,
    },
    guruNilai: {
        fontFamily: "Archivo-SemiBold",
        fontSize: 12,
        color: "#323232",
    },
    guruTanggal: {
        fontFamily: "Archivo-Regular",
        fontSize: 11,
        color: "#999",
    },
});

export default SurveyKarakterDetail;